import { supabase } from '../lib/supabase';

/**
 * Stripe Connect Service
 * Multi-tenant payment processing — each dealer connects their own Stripe account.
 * Uses Stripe Connect Standard accounts so dealers manage their own dashboard.
 *
 * Flow:
 * 1. Master admin onboards dealer -> dealer gets invite email
 * 2. Dealer creates account, connects Stripe via OAuth
 * 3. Customer buys shed -> payment goes to dealer's Stripe account
 * 4. Platform takes application fee (configurable %)
 */

const STRIPE_PUBLISHABLE_KEY = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || '';

class StripeConnectService {
  constructor() {
    this.stripePromise = null;
    this.platformFeePercent = parseFloat(import.meta.env.VITE_PLATFORM_FEE_PERCENT || '5');
  }

  async getStripe() {
    if (!this.stripePromise) {
      const { loadStripe } = await import('@stripe/stripe-js');
      this.stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);
    }
    return this.stripePromise;
  }

  // ==========================================
  // STRIPE CONNECT ONBOARDING
  // ==========================================

  getConnectOAuthUrl(dealerId, returnUrl) {
    const clientId = import.meta.env.VITE_STRIPE_CLIENT_ID || '';
    const redirectUri = returnUrl || `${window.location.origin}/dealer/stripe-callback`;

    const params = new URLSearchParams({
      response_type: 'code',
      client_id: clientId,
      scope: 'read_write',
      redirect_uri: redirectUri,
      state: dealerId,
      'stripe_user[business_type]': 'company',
    });

    return `https://connect.stripe.com/oauth/authorize?${params.toString()}`;
  }

  async completeStripeConnect(code, dealerId) {
    try {
      const { data, error } = await supabase.functions.invoke('stripe-connect-oauth', {
        body: { code, dealer_id: dealerId }
      });
      if (error) throw error;

      if (data?.stripe_account_id) {
        await supabase
          .from('dealers')
          .update({
            stripe_account_id: data.stripe_account_id,
            stripe_connected: true,
            stripe_connected_at: new Date().toISOString(),
            stripe_account_type: 'standard'
          })
          .eq('id', dealerId);
      }
      return { data, error: null };
    } catch (err) {
      console.error('Stripe Connect OAuth Error:', err);
      return { data: null, error: err };
    }
  }

  async createAccountLink(dealerId) {
    try {
      const { data, error } = await supabase.functions.invoke('stripe-create-account-link', {
        body: {
          dealer_id: dealerId,
          return_url: `${window.location.origin}/dealer/stripe-callback?dealer_id=${dealerId}`,
          refresh_url: `${window.location.origin}/dealer/stripe-refresh?dealer_id=${dealerId}`
        }
      });
      if (error) throw error;
      return { data, error: null };
    } catch (err) {
      console.error('Create Account Link Error:', err);
      return { data: null, error: err };
    }
  }

  async checkStripeStatus(dealerId) {
    try {
      const { data, error } = await supabase
        .from('dealers')
        .select('stripe_account_id, stripe_connected, stripe_connected_at, stripe_publishable_key, stripe_secret_key')
        .eq('id', dealerId)
        .single();
      if (error) throw error;
      return {
        connected: data?.stripe_connected || false,
        accountId: data?.stripe_account_id || null,
        connectedAt: data?.stripe_connected_at || null,
        publishableKey: data?.stripe_publishable_key || '',
        secretKey: data?.stripe_secret_key || ''
      };
    } catch (err) {
      console.error('Check Stripe Status Error:', err);
      return { connected: false, accountId: null, connectedAt: null, publishableKey: '', secretKey: '' };
    }
  }

  async saveStripeKeys(dealerId, publishableKey, secretKey) {
    try {
      const { data, error } = await supabase
        .from('dealers')
        .update({
          stripe_publishable_key: publishableKey,
          stripe_secret_key: secretKey,
          stripe_connected: !!(publishableKey && secretKey),
          stripe_connected_at: (publishableKey && secretKey) ? new Date().toISOString() : null,
          stripe_account_type: 'direct'
        })
        .eq('id', dealerId)
        .select()
        .single();
      if (error) throw error;
      return { data, error: null };
    } catch (err) {
      console.error('Save Stripe Keys Error:', err);
      return { data: null, error: err };
    }
  }

  async disconnectStripe(dealerId) {
    try {
      const { data, error } = await supabase.functions.invoke('stripe-disconnect', {
        body: { dealer_id: dealerId }
      });
      if (error) throw error;

      await supabase
        .from('dealers')
        .update({
          stripe_account_id: null,
          stripe_connected: false,
          stripe_connected_at: null,
          stripe_publishable_key: null,
          stripe_secret_key: null
        })
        .eq('id', dealerId);
      return { data, error: null };
    } catch (err) {
      console.error('Disconnect Stripe Error:', err);
      return { data: null, error: err };
    }
  }

  // ==========================================
  // CHECKOUT / PAYMENT PROCESSING
  // ==========================================

  async createCheckoutSession({ shed, dealer, customerEmail, customerName, customerPhone, paymentType = 'deposit' }) {
    try {
      if (!dealer?.stripe_connected) {
        throw new Error('Dealer has not configured their Stripe API keys');
      }

      const isDeposit = paymentType === 'deposit';
      const amount = isDeposit
        ? (shed.depositAmount || Math.round((shed.salePrice || shed.price) * 0.25))
        : (shed.salePrice || shed.price);

      const amountInCents = Math.round(amount * 100);

      const { data, error } = await supabase.functions.invoke('stripe-create-checkout', {
        body: {
          shed_id: shed.id,
          dealer_id: dealer.id,
          amount: amountInCents,
          payment_type: paymentType,
          customer_email: customerEmail,
          customer_name: customerName || '',
          customer_phone: customerPhone || '',
          shed_name: shed.name || `${shed.size} ${shed.shedType}`,
          shed_image: shed.images?.[0] || null,
          success_url: `${window.location.origin}/payment-success?session_id={CHECKOUT_SESSION_ID}&dealer_id=${dealer.id}&shed_id=${shed.id}`,
          cancel_url: `${window.location.origin}/shed/${shed.id}?payment=cancelled`
        }
      });
      if (error) throw error;
      return { data, error: null };
    } catch (err) {
      console.error('Create Checkout Session Error:', err);
      return { data: null, error: err };
    }
  }

  async redirectToCheckout(sessionId, checkoutUrl = null) {
    try {
      // Prefer the direct checkout URL — works without client-side Stripe.js
      // and doesn't require the platform publishable key to match the dealer's acocount
      if (checkoutUrl) {
        window.location.href = checkoutUrl;
        return { error: null };
      }

      // Fallback: use Stripe.js redirect (requires matching publishable key)
      const stripe = await this.getStripe();
      if (!stripe) throw new Error('Stripe not loaded — no checkout URL available');
      const { error } = await stripe.redirectToCheckout({ sessionId });
      if (error) throw error;
      return { error: null };
    } catch (err) {
      console.error('Redirect to Checkout Error:', err);
      return { error: err };
    }
  }

  async checkout({ shed, dealer, customerEmail, customerName, customerPhone, paymentType = 'deposit' }) {
    const { data, error } = await this.createCheckoutSession({
      shed, dealer, customerEmail, customerName, customerPhone, paymentType
    });
    if (error) return { error };
    if (!data?.session_id && !data?.url) return { error: new Error('No session ID or checkout URL returned') };

    // Use the direct URL from Stripe (preferred) or fall back to session ID redirect
    return this.redirectToCheckout(data.session_id, data.url);
  }

  async getCheckoutSession(sessionId) {
    try {
      const { data, error } = await supabase.functions.invoke('stripe-get-session', {
        body: { session_id: sessionId }
      });
      if (error) throw error;
      return { data, error: null };
    } catch (err) {
      console.error('Get Checkout Session Error:', err);
      return { data: null, error: err };
    }
  }

  // ==========================================
  // PAYMENT RECORDS
  // ==========================================

  async recordPayment(paymentData) {
    try {
      const { data, error } = await supabase
        .from('payments')
        .insert([{
          dealer_id: paymentData.dealer_id,
          shed_id: paymentData.shed_id,
          customer_name: paymentData.customer_name,
          customer_email: paymentData.customer_email,
          amount: paymentData.amount,
          application_fee: paymentData.application_fee,
          net_amount: paymentData.amount - (paymentData.application_fee || 0),
          payment_type: paymentData.payment_type,
          status: paymentData.status || 'completed',
          stripe_session_id: paymentData.stripe_session_id,
          stripe_payment_intent_id: paymentData.stripe_payment_intent_id,
          processor: 'stripe_connect',
          metadata: paymentData.metadata || {}
        }])
        .select()
        .single();
      if (error) throw error;
      return { data, error: null };
    } catch (err) {
      console.error('Record Payment Error:', err);
      return { data: null, error: err };
    }
  }

  async getDealerPayments(dealerId, filters = {}) {
    try {
      let query = supabase
        .from('payments')
        .select('*')
        .eq('dealer_id', dealerId)
        .order('created_at', { ascending: false });
      if (filters.status) query = query.eq('status', filters.status);
      if (filters.payment_type) query = query.eq('payment_type', filters.payment_type);
      const { data, error } = await query;
      if (error) throw error;
      return { data, error: null };
    } catch (err) {
      console.error('Get Dealer Payments Error:', err);
      return { data: [], error: err };
    }
  }

  async getAllPayments(filters = {}) {
    try {
      let query = supabase
        .from('payments')
        .select('*, dealers(company_name, email)')
        .order('created_at', { ascending: false });
      if (filters.status) query = query.eq('status', filters.status);
      if (filters.dealer_id) query = query.eq('dealer_id', filters.dealer_id);
      const { data, error } = await query;
      if (error) throw error;
      return { data, error: null };
    } catch (err) {
      console.error('Get All Payments Error:', err);
      return { data: [], error: err };
    }
  }

  // ==========================================
  // REFUNDS
  // ==========================================

  async processRefund(paymentId, amount = null) {
    try {
      const { data, error } = await supabase.functions.invoke('stripe-refund', {
        body: { payment_id: paymentId, amount }
      });
      if (error) throw error;
      await supabase
        .from('payments')
        .update({
          status: amount ? 'partially_refunded' : 'refunded',
          refunded_at: new Date().toISOString(),
          refund_amount: amount
        })
        .eq('id', paymentId);
      return { data, error: null };
    } catch (err) {
      console.error('Process Refund Error:', err);
      return { data: null, error: err };
    }
  }

  // ==========================================
  // UTILITY
  // ==========================================

  async getDealerForShed(shedId) {
    try {
      const { data, error } = await supabase
        .from('sheds_inventory')
        .select('dealer_id, dealers(id, company_name, stripe_account_id, stripe_connected)')
        .eq('id', shedId)
        .single();
      if (error) throw error;
      return { data: data?.dealers || null, error: null };
    } catch (err) {
      console.error('Get Dealer For Shed Error:', err);
      return { data: null, error: err };
    }
  }

  calculatePlatformFee(amount) {
    return Math.round(amount * (this.platformFeePercent / 100) * 100) / 100;
  }
}

export const stripeConnectService = new StripeConnectService();
export default stripeConnectService;
